// usePointerMode などからの「ユーザー操作」を CanvasOp へ変換し、
// 楽観更新 (applyOptimistic) → realtime クライアント送信を一括で行う dispatcher。
//
// 1. clientOpId を生成
// 2. doc store の applyOptimistic でローカル即時反映（inFlightOps に積む）
// 3. realtime クライアント経由で op.apply 送信
// 4. サーバから op.commit が返ってきたら use-realtime-connection が onServerCommit を呼ぶ
//    → inFlightOps から外して確定スナップショットへ反映
// 5. op.reject が来た op だけが onServerReject 経由でロールバックされる

import { useCallback } from "react";
import type {
  CanvasOp,
  CanvasObject,
  PenStrokeCanvasObject,
  StickyCanvasObject,
  TextCanvasObject,
  ShapeCanvasObject,
  ImageCanvasObject,
  VideoCanvasObject,
  LinkCardCanvasObject,
} from "@refinement-canvas/domain";
import { customAlphabet } from "nanoid";
import { useCanvasDocStore } from "./store/canvas-doc-store";
import { useRealtimeClient } from "../../realtime/realtime-context";

const generateId = customAlphabet("23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ", 16);

interface DispatcherContext {
  readonly actorUserId: string;
}

// CanvasObject は discriminated union のため、Omit を distributive に展開して
// 個別 type の draft 入力（text / shapeKind / src 等）を保持できるようにする。
type DistributiveDraft<T> = T extends CanvasObject
  ? Omit<T, "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"> & {
      readonly id?: string | undefined;
    }
  : never;

export type CanvasObjectDraft = DistributiveDraft<CanvasObject>;

export interface CanvasOpDispatcher {
  readonly createObject: (object: CanvasObjectDraft) => CanvasObject;
  readonly transformObject: (
    objectId: string,
    transform: Partial<{
      x: number;
      y: number;
      w: number;
      h: number;
      rotation: number;
      zOrder: number;
    }>
  ) => void;
  readonly updateText: (objectId: string, text: string) => void;
  readonly updateAttachmentSrc: (objectId: string, src: string) => void;
  readonly deleteObject: (objectId: string) => void;
  readonly commitPenStroke: (
    points: ReadonlyArray<{ x: number; y: number; pressure: number }>,
    color: string,
    thickness: number,
    boundingBox: { x: number; y: number; w: number; h: number }
  ) => PenStrokeCanvasObject;
}

export function useCanvasOpDispatcher(ctx: DispatcherContext): CanvasOpDispatcher {
  const applyOptimistic = useCanvasDocStore((s) => s.applyOptimistic);
  const objects = useCanvasDocStore((s) => s.objects);
  const realtime = useRealtimeClient();

  const dispatch = useCallback(
    (op: CanvasOp): void => {
      applyOptimistic(op);
      realtime.sendOp(op);
    },
    [applyOptimistic, realtime]
  );

  const createObject = useCallback<CanvasOpDispatcher["createObject"]>(
    (input) => {
      const id = input.id ?? generateId();
      const now = new Date().toISOString();
      const nextZ = computeNextZOrder(objects);
      const object = {
        ...input,
        id,
        version: 1,
        zOrder: nextZ,
        createdAt: now,
        createdBy: ctx.actorUserId,
        updatedAt: now,
        updatedBy: ctx.actorUserId,
      } as CanvasObject;
      const op: CanvasOp = {
        type: "object.create",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        object,
      };
      dispatch(op);
      return object;
    },
    [ctx.actorUserId, dispatch, objects]
  );

  const transformObject = useCallback<CanvasOpDispatcher["transformObject"]>(
    (objectId, transform) => {
      const op: CanvasOp = {
        type: "object.transform",
        clientOpId: generateId(),
        clientCreatedAt: new Date().toISOString(),
        actorUserId: ctx.actorUserId,
        objectId,
        transform,
      };
      dispatch(op);
    },
    [ctx.actorUserId, dispatch]
  );

  const updateText = useCallback<CanvasOpDispatcher["updateText"]>(
    (objectId, text) => {
      const op: CanvasOp = {
        type: "object.text.update",
        clientOpId: generateId(),
        clientCreatedAt: new Date().toISOString(),
        actorUserId: ctx.actorUserId,
        objectId,
        text,
      };
      dispatch(op);
    },
    [ctx.actorUserId, dispatch]
  );

  const updateAttachmentSrc = useCallback<CanvasOpDispatcher["updateAttachmentSrc"]>(
    (objectId, src) => {
      const op: CanvasOp = {
        type: "object.attachment.update",
        clientOpId: generateId(),
        clientCreatedAt: new Date().toISOString(),
        actorUserId: ctx.actorUserId,
        objectId,
        src,
      };
      dispatch(op);
    },
    [ctx.actorUserId, dispatch]
  );

  const deleteObject = useCallback<CanvasOpDispatcher["deleteObject"]>(
    (objectId) => {
      const op: CanvasOp = {
        type: "object.delete",
        clientOpId: generateId(),
        clientCreatedAt: new Date().toISOString(),
        actorUserId: ctx.actorUserId,
        objectId,
      };
      dispatch(op);
    },
    [ctx.actorUserId, dispatch]
  );

  const commitPenStroke = useCallback<CanvasOpDispatcher["commitPenStroke"]>(
    (points, color, thickness, boundingBox) => {
      const id = generateId();
      const now = new Date().toISOString();
      const nextZ = computeNextZOrder(objects);
      const stroke: PenStrokeCanvasObject = {
        type: "pen-stroke",
        id,
        x: boundingBox.x,
        y: boundingBox.y,
        w: boundingBox.w,
        h: boundingBox.h,
        zOrder: nextZ,
        version: 1,
        color,
        thickness,
        points: [...points],
        createdAt: now,
        createdBy: ctx.actorUserId,
        updatedAt: now,
        updatedBy: ctx.actorUserId,
      };
      const op: CanvasOp = {
        type: "pen.stroke.commit",
        clientOpId: generateId(),
        clientCreatedAt: now,
        actorUserId: ctx.actorUserId,
        object: stroke,
      };
      dispatch(op);
      return stroke;
    },
    [ctx.actorUserId, dispatch, objects]
  );

  return {
    createObject,
    transformObject,
    updateText,
    updateAttachmentSrc,
    deleteObject,
    commitPenStroke,
  };
}

/** 既存オブジェクトの最大 zOrder + 1 を返す（簡易な「最前面」割り当て）。 */
function computeNextZOrder(objects: Record<string, CanvasObject>): number {
  let max = 0;
  for (const obj of Object.values(objects)) {
    if (obj.zOrder > max) max = obj.zOrder;
  }
  return max + 1;
}

// 補助型: createObject に渡す引数を 1 種類ずつ作るためのヘルパー。
export type StickyDraftInput = Omit<
  StickyCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type TextDraftInput = Omit<
  TextCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ShapeDraftInput = Omit<
  ShapeCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type ImageDraftInput = Omit<
  ImageCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type VideoDraftInput = Omit<
  VideoCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
export type LinkCardDraftInput = Omit<
  LinkCardCanvasObject,
  "id" | "version" | "createdAt" | "createdBy" | "updatedAt" | "updatedBy" | "zOrder"
>;
